import type { LogicScheduleNode, TradeLogicJsonObject, TradeLogicJsonValue } from './types';

export const scheduleSessionOptions = [
    { value: 'regular', label: 'Regular' },
    { value: 'extended', label: 'Extended' },
    { value: 'preMarket', label: 'Pre-market' },
    { value: 'postMarket', label: 'Post-market' }
];

export const scheduleDayOfWeekOptions = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
];

export interface ScheduleParameterInputState {
    anchor: string;
    calendar: string;
    customParametersJson: string;
    daysOfWeek: string[];
    endTime: string;
    endAnchor: string;
    intervalUnit: string;
    intervalValue: string;
    maxBars: string;
    maxDaysToExpiry: string;
    maxMinutes: string;
    minBars: string;
    minDaysToExpiry: string;
    minMinutes: string;
    offsetsJson: string;
    scheduleType: string;
    session: string;
    startTime: string;
    startAnchor: string;
    timezone: string;
    times: string;
}

export const buildScheduleParameterInputState = (parameters: TradeLogicJsonObject | null | undefined): ScheduleParameterInputState => ({
    anchor: readString(parameters?.anchor) || 'marketClose',
    calendar: readString(parameters?.calendar) || 'market',
    customParametersJson: JSON.stringify(parameters ?? {}, null, 2),
    daysOfWeek: readStringArray(parameters?.days ?? parameters?.daysOfWeek).map((day) => day.toLowerCase()),
    endTime: readString(parameters?.endTime),
    endAnchor: readString(parameters?.endAnchor) || 'marketClose',
    intervalUnit: readIntervalUnit(parameters?.interval) || 'minutes',
    intervalValue: readIntervalValue(parameters?.interval),
    maxBars: readNumberInput(parameters?.maxBars),
    maxDaysToExpiry: readNumberInput(parameters?.maxDaysToExpiry),
    maxMinutes: readNumberInput(parameters?.maxMinutes),
    minBars: readNumberInput(parameters?.minBars),
    minDaysToExpiry: readNumberInput(parameters?.minDaysToExpiry),
    minMinutes: readNumberInput(parameters?.minMinutes),
    offsetsJson: JSON.stringify(parameters?.offsets ?? [{ unit: 'minutes', value: -5 }], null, 2),
    scheduleType: readString(parameters?.scheduleType) || 'fixedTimes',
    session: readString(parameters?.session) || 'regular',
    startTime: readString(parameters?.startTime),
    startAnchor: readString(parameters?.startAnchor) || 'marketOpen',
    timezone: readString(parameters?.timezone) || 'America/New_York',
    times: readStringArray(parameters?.times).join(', ') || '15:55'
});

export const parseScheduleParameterInputState = (
    scheduleKind: LogicScheduleNode['scheduleKind'],
    state: ScheduleParameterInputState
): TradeLogicJsonObject => {
    switch (scheduleKind) {
        case 'marketSession':
            return {
                session: parseSession(state.session),
                timezone: normalizeOptionalString(state.timezone) ?? 'America/New_York'
            };
        case 'timeOfDay':
            return pruneNullishValues({
                endTime: parseOptionalTime(state.endTime, 'End time'),
                startTime: parseOptionalTime(state.startTime, 'Start time'),
                timezone: normalizeOptionalString(state.timezone) ?? 'America/New_York'
            });
        case 'dayOfWeek':
            return {
                daysOfWeek: normalizeDaysOfWeek(state.daysOfWeek)
            };
        case 'entryAge':
            return validateRangeParameters(
                pruneNullishValues({
                    maxBars: parseOptionalNonNegativeInteger(state.maxBars, 'Maximum bars'),
                    maxMinutes: parseOptionalNonNegativeInteger(state.maxMinutes, 'Maximum minutes'),
                    minBars: parseOptionalNonNegativeInteger(state.minBars, 'Minimum bars'),
                    minMinutes: parseOptionalNonNegativeInteger(state.minMinutes, 'Minimum minutes')
                }),
                [
                    ['minBars', 'maxBars', 'Minimum bars cannot exceed maximum bars'],
                    ['minMinutes', 'maxMinutes', 'Minimum minutes cannot exceed maximum minutes']
                ]
            );
        case 'optionExpiry':
            return validateRangeParameters(
                pruneNullishValues({
                    maxDaysToExpiry: parseOptionalNonNegativeInteger(state.maxDaysToExpiry, 'Maximum days to expiry'),
                    minDaysToExpiry: parseOptionalNonNegativeInteger(state.minDaysToExpiry, 'Minimum days to expiry')
                }),
                [['minDaysToExpiry', 'maxDaysToExpiry', 'Minimum days to expiry cannot exceed maximum days to expiry']]
            );
        case 'custom':
        default:
            return parseNativeCustomScheduleParameters(state);
    }
};

export const buildDefaultNativeScheduleParameters = (): TradeLogicJsonObject => ({
    scheduleType: 'fixedTimes',
    calendar: 'market',
    timezone: 'America/New_York',
    times: ['15:55']
});

export const validateScheduleParameters = (node: LogicScheduleNode): string | null => {
    try {
        parseScheduleParameterInputState(node.scheduleKind, buildScheduleParameterInputState(node.parameters));

        return null;
    } catch (error) {
        return error instanceof Error ? error.message : 'Schedule parameters are invalid';
    }
};

export const toggleScheduleDayOfWeek = (daysOfWeek: string[], day: string, isEnabled: boolean): string[] => {
    if (!scheduleDayOfWeekOptions.some((option) => option.value === day)) {
        return daysOfWeek;
    }

    if (isEnabled) {
        return scheduleDayOfWeekOptions
            .map((option) => option.value)
            .filter((optionDay) => optionDay === day || daysOfWeek.includes(optionDay));
    }

    return daysOfWeek.filter((optionDay) => optionDay !== day);
};

const parseCustomParametersJson = (rawValue: string): TradeLogicJsonObject => {
    const trimmedValue = rawValue.trim();

    if (trimmedValue === '') return {};

    let parsed: unknown;

    try {
        parsed = JSON.parse(trimmedValue);
    } catch {
        throw new Error('Custom parameters must be valid JSON');
    }

    if (!isTradeLogicJsonObject(parsed)) {
        throw new Error('Custom parameters must be a JSON object');
    }

    return parsed;
};

const parseNativeCustomScheduleParameters = (state: ScheduleParameterInputState): TradeLogicJsonObject => {
    const base = pruneNullishValues({
        calendar: parseCalendar(state.calendar),
        timezone: normalizeOptionalString(state.timezone) ?? 'America/New_York',
        days: state.daysOfWeek.length ? normalizeDaysOfWeek(state.daysOfWeek) : null
    });

    if (state.scheduleType === 'fixedTimes') {
        const times = parseTimes(state.times);

        return {
            ...base,
            scheduleType: 'fixedTimes',
            times
        };
    }

    if (state.scheduleType === 'everyDuration') {
        const intervalValue = parseRequiredPositiveInteger(state.intervalValue, 'Interval value');

        return {
            ...base,
            scheduleType: 'everyDuration',
            interval: {
                unit: parseIntervalUnit(state.intervalUnit),
                value: intervalValue
            },
            startAnchor: parseMarketAnchor(state.startAnchor, 'Start anchor'),
            endAnchor: parseMarketAnchor(state.endAnchor, 'End anchor')
        };
    }

    if (state.scheduleType === 'marketOffset') {
        return {
            ...base,
            scheduleType: 'marketOffset',
            anchor: parseMarketAnchor(state.anchor, 'Anchor'),
            offsets: parseOffsetsJson(state.offsetsJson)
        };
    }

    return parseCustomParametersJson(state.customParametersJson);
};

const parseSession = (value: string): string => {
    if (scheduleSessionOptions.some((option) => option.value === value)) {
        return value;
    }

    throw new Error('Session is not supported');
};

const parseCalendar = (value: string): string => {
    if (value === 'market' || value === 'calendar') {
        return value;
    }

    throw new Error('Schedule calendar must be market or calendar');
};

const parseIntervalUnit = (value: string): string => {
    if (value === 'seconds' || value === 'minutes' || value === 'hours') {
        return value;
    }

    throw new Error('Interval unit must be seconds, minutes, or hours');
};

const parseMarketAnchor = (value: string, label: string): string => {
    if (value === 'sessionOpen' || value === 'marketOpen' || value === 'marketClose' || value === 'sessionClose') {
        return value;
    }

    throw new Error(`${label} is not supported`);
};

const normalizeDaysOfWeek = (daysOfWeek: string[]): string[] =>
    scheduleDayOfWeekOptions
        .map((option) => option.value)
        .filter((dayOfWeek) => daysOfWeek.includes(dayOfWeek))
        .map((dayOfWeek) => `${dayOfWeek.charAt(0).toUpperCase()}${dayOfWeek.slice(1)}`);

const validateRangeParameters = (
    parameters: TradeLogicJsonObject,
    ranges: [minKey: string, maxKey: string, errorMessage: string][]
): TradeLogicJsonObject => {
    for (const [minKey, maxKey, errorMessage] of ranges) {
        const minValue = parameters[minKey];
        const maxValue = parameters[maxKey];

        if (typeof minValue === 'number' && typeof maxValue === 'number' && minValue > maxValue) {
            throw new Error(errorMessage);
        }
    }

    return parameters;
};

const parseOptionalNonNegativeInteger = (rawValue: string, label: string): number | null => {
    const trimmedValue = rawValue.trim();

    if (trimmedValue === '') return null;

    const parsed = Number(trimmedValue);

    if (!Number.isInteger(parsed)) {
        throw new Error(`${label} must be an integer`);
    }

    if (parsed < 0) {
        throw new Error(`${label} must be zero or greater`);
    }

    return parsed;
};

const parseRequiredPositiveInteger = (rawValue: string, label: string): number => {
    const parsed = parseOptionalNonNegativeInteger(rawValue, label);

    if (parsed === null || parsed < 1) {
        throw new Error(`${label} must be one or greater`);
    }

    return parsed;
};

const parseOptionalTime = (rawValue: string, label: string): string | null => {
    const value = normalizeOptionalString(rawValue);

    if (value === null) return null;

    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) {
        throw new Error(`${label} must use HH:MM format`);
    }

    return value;
};

const parseTimes = (rawValue: string): string[] => {
    const times = rawValue
        .split(/[,\n]/)
        .map((time) => time.trim())
        .filter(Boolean);

    if (!times.length) {
        throw new Error('At least one fixed time is required');
    }

    for (const time of times) {
        parseOptionalTime(time, 'Fixed time');
    }

    return times;
};

const parseOffsetsJson = (rawValue: string): TradeLogicJsonValue[] => {
    const trimmedValue = rawValue.trim();

    if (!trimmedValue) {
        throw new Error('At least one market offset is required');
    }

    const parsed = JSON.parse(trimmedValue) as TradeLogicJsonValue;

    if (!Array.isArray(parsed)) {
        throw new Error('Market offsets must be a JSON array');
    }

    for (const offset of parsed) {
        if (!isTradeLogicJsonObject(offset)) {
            throw new Error('Each market offset must be a JSON object');
        }

        parseIntervalUnit(readString(offset.unit));

        if (typeof offset.value !== 'number') {
            throw new Error('Each market offset requires a numeric value');
        }
    }

    return parsed;
};

const pruneNullishValues = (value: Record<string, TradeLogicJsonValue | null>): TradeLogicJsonObject =>
    Object.fromEntries(Object.entries(value).filter((entry): entry is [string, TradeLogicJsonValue] => entry[1] !== null));

const normalizeOptionalString = (value: string): string | null => {
    const trimmedValue = value.trim();

    return trimmedValue ? trimmedValue : null;
};

const readNumberInput = (value: TradeLogicJsonValue | undefined): string => (typeof value === 'number' ? String(value) : '');

const readString = (value: TradeLogicJsonValue | undefined): string => (typeof value === 'string' ? value : '');

const readStringArray = (value: TradeLogicJsonValue | undefined): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const readIntervalUnit = (value: TradeLogicJsonValue | undefined): string => {
    if (!isTradeLogicJsonObject(value)) {
        return '';
    }

    return readString(value.unit);
};

const readIntervalValue = (value: TradeLogicJsonValue | undefined): string => {
    if (!isTradeLogicJsonObject(value)) {
        return '';
    }

    return readNumberInput(value.value);
};

const isTradeLogicJsonObject = (value: unknown): value is TradeLogicJsonObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
